import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { map, tap } from 'rxjs/internal/operators';
import { LoginVerificationChallenge } from 'src/app/models/login-verification-model/login-verification-challenge.model';
import { BoService } from 'src/app/services/bo-service/bo.service';
import { CommunicationService } from 'src/app/services/communication-service/communication.service';
import { NotificationsService } from 'src/app/services/notifications-services/notifications.service';
import { UserService } from 'src/app/services/user-service/user.service';
import { UserStorageService } from 'src/app/services/user-storage-service/user-storage.service';

@Component({
  selector: 'app-login-verification-code-card',
  templateUrl: './login-verification-code-card.component.html',
  styleUrls: ['./login-verification-code-card.component.scss']
})
export class LoginVerificationCodeCardComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('codeInput', { static: false }) codeInput: ElementRef;

  public code = ['', '', '', '', '', ''];
  public activeIndex = 0;
  public errorMessage: string;
  public inputFocused = false;
  public verifying = false;
  private challenge: LoginVerificationChallenge;
  private ignoreNextInput = false;
  private verifyCodeSubscription: Subscription;
  private selectCompanySubscription: Subscription;

  constructor(private userService: UserService,
              private userStorageService: UserStorageService,
              private router: Router,
              private boService: BoService,
              private notificationsService: NotificationsService,
              private communicationService: CommunicationService) { }

  ngOnInit() {
    this.challenge = this.userStorageService.getLoginVerificationChallenge();
    if (!this.challenge || !this.challenge.ChallengeId) {
      this.redirectToExpiredLogin();
    }
  }

  ngAfterViewInit() {
    if (this.challenge && this.challenge.ChallengeId) {
      this.focusInput();
    }
  }

  public onInput(event) {
    if (this.ignoreNextInput) {
      this.ignoreNextInput = false;
      event.target.value = '';
      return;
    }

    const value = this.cleanCodeValue(event.target.value);
    event.target.value = '';
    if (!value) {
      return;
    }
    if (value.length > 1) {
      this.applyCode(value);
      return;
    }

    this.insertCharacter(value);
  }

  public onPaste(event) {
    event.preventDefault();
    const clipboardData = event.clipboardData || window['clipboardData'];
    const pastedCode = clipboardData ? clipboardData.getData('text') : '';
    this.applyCode(pastedCode);
  }

  public onKeydown(event) {
    if (event.metaKey || event.ctrlKey || event.altKey || event.key === 'Tab') {
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();
      if (this.code[this.activeIndex]) {
        this.setCodeValue(this.activeIndex, '');
      } else if (this.activeIndex > 0) {
        this.activeIndex--;
        this.setCodeValue(this.activeIndex, '');
      }
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();
      this.setCodeValue(this.activeIndex, '');
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.activeIndex = Math.max(this.activeIndex - 1, 0);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.activeIndex = Math.min(this.activeIndex + 1, this.code.length - 1);
      return;
    }

    if (event.key.length === 1 && this.cleanCodeValue(event.key)) {
      event.preventDefault();
      this.ignoreNextInput = true;
      this.insertCharacter(event.key);
      setTimeout(() => this.ignoreNextInput = false);
      return;
    }

    if (event.key.length === 1) {
      event.preventDefault();
    }
  }

  public goBackToContacts() {
    this.router.navigate(['/signin/login-verification-contact']);
  }

  public focusCodeInput(index) {
    this.activeIndex = index;
    this.focusInput();
  }

  private applyCode(value) {
    const cleanValue = this.cleanCodeValue(value).slice(0, 6);
    for (let i = 0; i < this.code.length; i++) {
      this.code[i] = cleanValue[i] || '';
    }
    this.activeIndex = Math.min(cleanValue.length, this.code.length - 1);
    this.focusInput();
    this.verifyWhenComplete();
  }

  private insertCharacter(value) {
    this.setCodeValue(this.activeIndex, value);
    if (this.activeIndex < this.code.length - 1) {
      this.activeIndex++;
    }
    this.focusInput();
    this.verifyWhenComplete();
  }

  private verifyWhenComplete() {
    if (this.verifying) {
      return;
    }
    const verificationCode = this.code.join('');
    if (verificationCode.length === 6) {
      this.verifyCode(verificationCode);
    }
  }

  private verifyCode(verificationCode) {
    this.errorMessage = null;
    this.verifying = true;
    this.verifyCodeSubscription = this.userService.verifyLoginVerificationCode(
      this.userStorageService.getLang(),
      this.userStorageService.getUDID(),
      this.challenge.ChallengeId,
      verificationCode
    ).subscribe(
      (res) => {
        this.verifying = false;
        if (res && res['data']) {
          this.userStorageService.deleteLoginVerificationChallenge();
          this.handleLoginResponse(res);
        } else {
          this.errorMessage = 'signin-page.snackbar messages.wrong verification code';
        }
      },
      (error) => {
        this.verifying = false;
        this.errorMessage = this.resolveErrorMessage(error, 'signin-page.snackbar messages.wrong verification code');
        this.clearCode();
      }
    );
  }

  private handleLoginResponse(res) {
    const tmpContext = res['data']['context'];
    const companies = res['data']['companies'];
    this.userStorageService.setCompanyList(companies);
    this.userStorageService.setTmpContext(tmpContext);
    if (companies.length === 1) {
      this.selectCompany(companies[0].companyCode);
    } else if (companies.length > 1) {
      this.router.navigate(['/signin/account-selector']);
    } else {
      this.errorMessage = 'signin-page.snackbar messages.empty companies';
    }
  }

  private selectCompany(companyCode: string) {
    this.selectCompanySubscription = this.userService.selectCompany(
      this.userStorageService.getTmpContext(),
      this.userStorageService.getLang(),
      this.userStorageService.getUDID(),
      companyCode).subscribe(
      (res) => {
        if (res) {
          this.setUserStorageData(res['data']);
          this.getVersion();
          this.router.navigate(['']);
          this.socketsConnect();
        }
      },
      (error) => {
        this.errorMessage = this.resolveErrorMessage(error, 'signin-page.snackbar messages.wrong credentials');
      }
    );
  }

  private setUserStorageData(data) {
    this.userStorageService.setCompanyData(data['companyData']);
    this.userStorageService.setContext(data['context']);
    this.userStorageService.setMenuSectionList(data['menuOptions']);
    this.userStorageService.setUserData(data['userData']);
    this.userStorageService.setCenterList(data['centerList']);
  }

  private getVersion() {
    this.boService.getVersion().pipe(
      tap(o => {
        if (this.userStorageService.setBOVersion(o)) {
          if (this.boService.newVersionAvailable()) {
            this.notificationsService.notificate('fas fa-sync-alt', 'New version available', false, 'window.location.reload(true);');
          }
        }
      })
    );
  }

  private socketsConnect() {
    this.communicationService.getSocketChannels().pipe(
      map(socketChannels => this.userStorageService.setSocketChannels(socketChannels['channelList'])),
      map(socketChannels => this.userStorageService.getSocketChannels().filter(sc => sc.FlagCompany === true)[0]),
      map(socketChannel => this.communicationService.listenCommunications(socketChannel))
    ).subscribe();
  }

  private clearCode() {
    this.code = ['', '', '', '', '', ''];
    this.activeIndex = 0;
    this.focusInput();
  }

  private setCodeValue(index, value) {
    this.errorMessage = null;
    this.code[index] = this.cleanCodeValue(value).slice(0, 1);
  }

  private focusInput() {
    if (!this.codeInput) {
      return;
    }
    this.codeInput.nativeElement.focus();
    this.codeInput.nativeElement.value = '';
  }

  private cleanCodeValue(value) {
    return (value || '').replace(/[^a-zA-Z0-9]/g, '');
  }

  private redirectToExpiredLogin() {
    this.userStorageService.deleteLoginVerificationChallenge();
    this.router.navigate(['/signin'], { queryParams: { loginVerificationExpired: true } });
  }

  private resolveErrorMessage(error, fallback) {
    if (error && error['error'] && error['error']['message']) {
      return error['error']['message'];
    }
    return fallback;
  }

  ngOnDestroy() {
    if (this.verifyCodeSubscription) { this.verifyCodeSubscription.unsubscribe(); }
    if (this.selectCompanySubscription) { this.selectCompanySubscription.unsubscribe(); }
  }
}
